// src/hooks/useStandards.js
import { useState, useEffect, useMemo } from 'react';

/** Dynamic import by subject (code-split per subject) */
const loadStandardsModule = (subject) => {
  switch (subject) {
    // --- Arts ---
    case 'Visual Arts':
      return import(/* webpackChunkName: "standards-visual-arts" */ '../config/standards/visualArts.js');
    case 'Music':
      return import(/* webpackChunkName: "standards-music" */ '../config/standards/music.js');
    // Uncomment when added:
    // case 'Theater':
    //   return import(/* webpackChunkName: "standards-theater" */ '../config/standards/theater.js');
    // case 'Media Arts':
    //   return import(/* webpackChunkName: "standards-media-arts" */ '../config/standards/mediaArts.js');

    // --- Core ---
    case 'Mathematics':
      return import(/* webpackChunkName: "standards-mathematics" */ '../config/standards/mathematics.js');
    case 'English Language Arts (ELA)':
      return import(/* webpackChunkName: "standards-ela" */ '../config/standards/ela.js');
    case 'Science':
      return import(/* webpackChunkName: "standards-science" */ '../config/standards/science.js');

    // --- History (C3-aligned) ---
    case 'History':
      return import(/* webpackChunkName: "standards-history" */ '../config/standards/history.js');

    // --- Geography ---
    case 'Geography':
      return import(/* webpackChunkName: "standards-geography" */ '../config/standards/geography.js');

    // --- Student services / wellness ---
    case 'Occupational Therapy':
      return import(/* webpackChunkName: "standards-ot" */ '../config/standards/occupationalTherapy.js');
    case 'Physical Education':
      return import(/* webpackChunkName: "standards-pe" */ '../config/standards/physicalEducation.js');
    case 'Dance':
      return import(/* webpackChunkName: "standards-dance" */ '../config/standards/dance.js');
    case 'Health':
      return import(/* webpackChunkName: "standards-health" */ '../config/standards/health.js');
    case 'SEL (Social Emotional Learning)':
      return import(/* webpackChunkName: "standards-sel" */ '../config/standards/sel.js');

    // --- Technology strands ---
    case 'Computer Science':
      return import(/* webpackChunkName: "standards-computer-science" */ '../config/standards/computerScience.js');
    case 'Coding':
      return import(/* webpackChunkName: "standards-coding" */ '../config/standards/coding.js');
    case 'Digital Citizenship':
      return import(/* webpackChunkName: "standards-digital-citizenship" */ '../config/standards/digitalCitizenship.js');
    case 'Digital Literacy':
      return import(/* webpackChunkName: "standards-digital-literacy" */ '../config/standards/digitalLiteracy.js');
    case 'ISTE':
      return import(/* webpackChunkName: "standards-iste" */ '../config/standards/iste.js');

    // --- NEW: Special Education (only addition) ---
    case 'Special Education':
      return import(/* webpackChunkName: "standards-special-education" */ '../config/standards/specialEducation.js');

    // --- NEW: Speech & Language ---
    case 'Speech & Language':
      return import(/* webpackChunkName: "standards-speech-language" */ '../config/standards/speechLanguage.js');

    // Optional umbrella
    case 'Technology':
      return import(/* webpackChunkName: "standards-technology" */ '../config/standards/technology.js');
   // --- Technology strands ---
   case 'ISTE':
     return import(/* webpackChunkName: "standards-iste" */ '../config/standards/iste.js');

 // --- NEW subjects ---
   case 'Business':
      return import(/* webpackChunkName: "standards-business" */ '../config/standards/business.js');
   case 'Careers':
      return import(/* webpackChunkName: "standards-careers" */ '../config/standards/careers.js');

    // --- ESL / ELL ---
    case 'ESL / ELL':
      return import(/* webpackChunkName: "standards-esl" */ '../config/standards/esl.js');

    default:
      return Promise.resolve(null);
  }
};

export const useStandards = (primarySubject, gradeLevels, selectedDomains) => {
  const [standardsData, setStandardsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!primarySubject) {
      setStandardsData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    loadStandardsModule(primarySubject)
      .then((module) => {
        console.log('[useStandards] Module loaded for subject:', primarySubject, module);
        if (!isMounted) return;
        if (module && module.default) {
          const root = module.default;
          console.log('[useStandards] Root keys:', Object.keys(root));
          console.log('[useStandards] Looking for key:', primarySubject, '— found?', !!root[primarySubject]);

          // Back-compat subject aliases
          const SUBJECT_ALIASES = {
            'History': [
              'Social Studies - History',
              'Social Studies–History',
              'Social Studies—History',
              'Social Studies'
            ],
            'Geography': [
              'Social Studies - Geography',
              'Social Studies–Geography',
              'Social Studies—Geography',
              'Geography (NCGE)'
            ]
          };

          let subjectData = root[primarySubject];

          if (!subjectData) {
            // case-insensitive exact match first
            const ci = Object.keys(root).find(k => k.toLowerCase() === primarySubject.toLowerCase());
            if (ci) subjectData = root[ci];
          }
          if (!subjectData && SUBJECT_ALIASES[primarySubject]) {
            for (const alias of SUBJECT_ALIASES[primarySubject]) {
              if (root[alias]) { subjectData = root[alias]; break; }
              const ciAlias = Object.keys(root).find(k => k.toLowerCase() === alias.toLowerCase());
              if (ciAlias) { subjectData = root[ciAlias]; break; }
            }
          }

          setStandardsData(subjectData || null);
        } else {
          setStandardsData(null);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error(`Failed to load standards for ${primarySubject}:`, err);
        setError(err);
        setStandardsData(null);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => { isMounted = false; };
  }, [primarySubject]);

  const filteredStandards = useMemo(() => {
    if (!standardsData || !selectedDomains?.length) return [];

    // If no specific grade selected or “Not Grade Specific”, include all grades
    const noGradeFilter = !gradeLevels?.length || gradeLevels.includes('Not Grade Specific');

    const availableGradeKeys = Object.keys(standardsData);

    // ✅ only treat as banded if actual band keys exist (NOT just "High School")
    const usesBands = ['K-2','K–2','3-5','3–5','6-8','6–8','K-12','K–12']
      .some(k => standardsData.hasOwnProperty(k));

    // Helper to coalesce band key variants
    const coalesceBandKey = (band) => {
      const variants = [band, band.replace('–', '-'), band.replace('-', '–')];
      for (const v of variants) if (standardsData.hasOwnProperty(v)) return v;
      if (standardsData.hasOwnProperty('K-12')) return 'K-12';
      if (standardsData.hasOwnProperty('K–12')) return 'K–12';
      return band;
    };

    const normalizeGradeKey = (grade) => {
      // ELA has two HS buckets
      if (primarySubject === 'English Language Arts (ELA)') {
        if (grade === '9th Grade' || grade === '10th Grade') return 'High School (9-10)';
        if (grade === '11th Grade' || grade === '12th Grade') return 'High School (11-12)';
        return grade;
      }

      // Math collapses 9–12
      if (primarySubject === 'Mathematics') {
        if (['9th Grade','10th Grade','11th Grade','12th Grade','High School'].includes(grade)) {
          return 'High School';
        }
        return grade;
      }

      // If this file uses bands, map elementary/middle/HS to band keys
      if (usesBands) {
        if (['Kindergarten','1st Grade','2nd Grade'].includes(grade)) return coalesceBandKey('K-2');
        if (['3rd Grade','4th Grade','5th Grade'].includes(grade)) return coalesceBandKey('3-5');
        if (['6th Grade','7th Grade','8th Grade'].includes(grade)) return coalesceBandKey('6-8');
        if (['9th Grade','10th Grade','11th Grade','12th Grade','High School'].includes(grade)) return coalesceBandKey('High School');
        const k12 = coalesceBandKey('K-12');
        if (standardsData.hasOwnProperty(k12)) return k12;
        return grade;
      }

      // Generic subjects with a single "High School" key
      if (['9th Grade','10th Grade','11th Grade','12th Grade'].includes(grade)) {
        if (standardsData.hasOwnProperty('High School')) return 'High School';
      }

      // Use exact key if present
      if (standardsData.hasOwnProperty(grade)) return grade;

      // If user selected generic "High School" but subject splits HS into bands (ELA)
      if (grade === 'High School') {
        const hsBands = ['High School (9-10)', 'High School (11-12)'].filter(k => standardsData.hasOwnProperty(k));
        if (hsBands.length) return hsBands; // expand later
        return 'High School';
      }

      return grade;
    };

    // Domain slug helper for forgiving matching (& vs and, punctuation, spacing, case)
    const slug = (s) =>
      s.toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim()
        .replace(/\s+/g, ' ');

    const normalizeDomain = (domainWithAbbr, sampleGradeKey) => {
      let key = domainWithAbbr.split(' (')[0].trim();

      // NGSS historical mismatch guard
      if (primarySubject === 'Science' && key === 'Engineering Design') {
        key = 'Engineering, Technology, and Applications of Science';
      }

      // Try exact/slug match on the provided grade’s keys
      if (standardsData && sampleGradeKey) {
        const sample = standardsData[sampleGradeKey];
        if (sample && typeof sample === 'object') {
          if (Object.prototype.hasOwnProperty.call(sample, key)) return key;
          const want = slug(key);
          const domainKeys = Object.keys(sample);
          const found = domainKeys.find(d => slug(d) === want);
          if (found) return found;
        }
      }

      // Fallback: scan any grade bucket for a slug match
      const anyGradeKey = availableGradeKeys[0];
      const anyGrade = standardsData[anyGradeKey] || {};
      const want = slug(key);
      const found = Object.keys(anyGrade).find(d => slug(d) === want);
      return found || key;
    };

    // Build grade keys to search
    let gradeKeysInData = [];
    if (noGradeFilter) {
      gradeKeysInData = availableGradeKeys;
    } else {
      const expanded = [];
      gradeLevels.forEach((g) => {
        const norm = normalizeGradeKey(g);
        if (Array.isArray(norm)) expanded.push(...norm);
        else expanded.push(norm);
      });
      gradeKeysInData = Array.from(new Set(expanded.filter(k => standardsData.hasOwnProperty(k))));
      // safety net: if nothing matched, fall back to all
      if (!gradeKeysInData.length) gradeKeysInData = availableGradeKeys;
    }

    const results = [];
    gradeKeysInData.forEach((gradeKey) => {
      const gradeData = standardsData[gradeKey];
      if (!gradeData) return;

      selectedDomains.forEach((domainRaw) => {
        const domainKey = normalizeDomain(domainRaw, gradeKey);
        const domainData = gradeData[domainKey];
        if (!domainData) return;

        if (Array.isArray(domainData)) {
          results.push(
            ...domainData.map((std) => ({ ...std, grade: gradeKey, domain: domainKey, cluster: null }))
          );
        } else if (typeof domainData === 'object' && domainData !== null) {
          // clustered (e.g., HS Proficient/Accomplished/Advanced)
          Object.keys(domainData).forEach((cluster) => {
            const clusterStandards = domainData[cluster];
            if (Array.isArray(clusterStandards)) {
              results.push(
                ...clusterStandards.map((std) => ({ ...std, grade: gradeKey, domain: domainKey, cluster }))
              );
            }
          });
        }
      });
    });

    // de-dupe by id
    const byId = new Map();
    results.forEach((s) => { if (!byId.has(s.id)) byId.set(s.id, s); });
    return Array.from(byId.values());
  }, [standardsData, gradeLevels, selectedDomains, primarySubject]);

  return { filteredStandards, isLoading, error };
};